import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Flex,
  Button,
  GridItem,
  Text,
  Container,
  useTheme,
} from '@chakra-ui/react';

import { DeptGrid } from '@repo/ui-components/dept/design-system';
import CardBlogStandard from '@repo/ui-components/dept/atoms/CardBlogStandard';
import BlogExpertBadge from '@repo/ui-components/tailwind/BlogExpertBadge';
import Link from '@repo/ui-components/tailwind/Link';
import Image from '@repo/ui-components/tailwind/Image';

import { generateSectionId } from '../../../utils/generateSectionId';

function FeaturedBlogExplore({ block }) {
  const theme = useTheme();

  return (
    <Box
      bgSize="cover"
      bgRepeat="no-repeat"
      backgroundImage={{ base: 'none', lg: theme.images?.greyBackground }}
      backgroundColor="neutral.50"
      pos="relative"
      {...generateSectionId(block)}
    >
      <Container>
        <Box py={[14, 20]}>
          <Flex
            justifyContent="space-between"
            alignItems="flex-end"
            pb={[8, null, 10]}
          >
            <Box>
              <Text as="h2" textStyle="h2" fontWeight="black" m={0}>
                {block.title}
              </Text>
              <Text
                as="h3"
                textStyle="h3"
                fontWeight="black"
                color="typography.light"
                mt={2}
              >
                {block.byline}
              </Text>
            </Box>
            {block.cta?.text && (
              <Button
                as={Link}
                className="font-semibold"
                link={block?.cta?.link}
                display={['none', null, 'inline-flex']}
                variant="primary"
              >
                {block.cta.text}
              </Button>
            )}
          </Flex>
          <DeptGrid gridRowGap={0}>
            <GridItem colSpan={{ base: 12, lg: 8 }}>
              {block?.posts
                ?.filter(({ postReference }) => !!postReference)
                .map(
                  post =>
                    post.featured && (
                      <CardBlogStandard
                        key={post.postReference._id}
                        article={post.postReference}
                        badge={post.badge}
                      />
                    )
                )}
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 4 }} pt={6}>
              {block?.posts
                ?.filter(post => !!post?.postReference)
                .map(
                  post =>
                    !post?.featured && (
                      <Flex
                        key={post.postReference._id}
                        gap={6}
                        alignItems="center"
                        py={2}
                        borderY="1px solid "
                        borderColor="stroke.light"
                      >
                        <Box
                          as={Link}
                          className="py-2"
                          link={`/blog/${post.postReference?.slug?.current}`}
                        >
                          {post.postReference?.image?.asset?.url && (
                            <Image
                              className="rounded-lg"
                              sanityImage={post.postReference.image}
                              srcSetBasisWidth={104}
                              width="104px"
                            />
                          )}
                        </Box>
                        <Flex flexDirection="column" alignItems="flex-start">
                          <Flex flexDirection="row" flexWrap="wrap">
                            {post.postReference?.categories?.map(
                              (category, index) => (
                                <Link
                                  className="mb-2 mr-1 font-bold text-muted"
                                  key={category._id || category._key}
                                  link={`/blog/${category.slug?.current}`}
                                >
                                  {category.title}
                                  {index !==
                                    post.postReference.categories.length - 1 &&
                                    ' • '}
                                </Link>
                              )
                            )}
                          </Flex>

                          <Text
                            as={Link}
                            className="text-secondary font-bold"
                            link={`/blog/${post.postReference?.slug?.current}`}
                          >
                            {post.postReference?.title}
                          </Text>
                          {post.badge?.headline && (
                            <BlogExpertBadge
                              className="mt-2"
                              author={post.postReference?.author?.title}
                              slug={post.postReference?.author?.slug?.current}
                              badge={post.badge}
                            />
                          )}
                        </Flex>
                      </Flex>
                    )
                )}
              {block?.cta?.text && (
                <Button
                  as={Link}
                  className="w-full font-semibold mt-6"
                  link={block?.cta?.link}
                  display={['inline-flex', null, 'none']}
                  variant="primary"
                >
                  {block.cta.text}
                </Button>
              )}
            </GridItem>
          </DeptGrid>
        </Box>
      </Container>
    </Box>
  );
}

FeaturedBlogExplore.propTypes = {
  block: PropTypes.shape({
    title: PropTypes.string,
    byline: PropTypes.string,
    cta: PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.shape({
        internalLink: PropTypes.shape({
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        }),
      }),
    }),
    posts: PropTypes.arrayOf(
      PropTypes.shape({
        featured: PropTypes.bool,

        badge: PropTypes.shape({
          headline: PropTypes.string,
          copy: PropTypes.string,
          link: PropTypes.shape({
            linkType: PropTypes.string,
            href: PropTypes.string,
            internalLink: PropTypes.shape({
              slug: PropTypes.shape({
                current: PropTypes.string,
              }),
            }),
          }),
          icon: PropTypes.shape({
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
          }),
        }),
        postReference: PropTypes.shape({
          title: PropTypes.string,
          image: PropTypes.shape({
            alt: PropTypes.string,
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
          }),
          categories: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string,
              slug: PropTypes.shape({
                current: PropTypes.string,
              }),
            })
          ),
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
          author: PropTypes.shape({
            title: PropTypes.string,
            slug: PropTypes.shape({
              current: PropTypes.string,
            }),
          }),
        }),
      })
    ),
  }),
}.isRequired;

export default FeaturedBlogExplore;
